/**
 * Hero Marquee - Infinite bidirectional scrolling wallpaper wall
 *
 * Handles:
 * - Duplicating card sets for seamless infinite scroll
 * - Hover-pause is handled in CSS (per row, via `.hero-marquee__row:hover`)
 * - "Shuffle vibes" button: loads the next batch of products via AJAX
 * - Mobile single-row angled scrolling
 */
class HeroMarquee {
  constructor(element) {
    this.element = element;
    this.tracks = element.querySelectorAll('.hero-marquee__track');
    this.inners = [];
    this.rows = [];
    this.isShuffling = false;

    this.perRow = parseInt(element.dataset.perRow, 10) || 12;
    this.batchSize = this.perRow * 2;
    this.pool = [];
    this.poolIndex = this.batchSize; // start *after* the initial server-rendered batch
    this.poolReady = false;

    // The storefront collection shown in the marquee is chosen RANDOMLY (with
    // weights) on the CLIENT side, in `pickRandomCollection()` below. It must NOT
    // be a Liquid/server-side pick: Shopify caches the server-rendered section,
    // so a `now`-based pick would be frozen per cache and the marquee would show
    // the same collection on every refresh. The JS reads the candidate list from
    // `data-collection-options` and picks one, so the choice varies on every
    // page load. There is no client-side sort variety — we simply display the
    // chosen collection in its own curated order. "Shuffle vibes" then behaves
    // like a true *next page* within the SAME collection's product pool.

    // Pick a random collection and load its pre-inlined pool synchronously, so
    // the chosen set shows immediately with no network round-trip. Fall back to
    // the legacy single inline pool if the pick fails.
    this.pickRandomCollection();
    if (!this.poolReady) {
      this.loadPoolInline();
    }

    this.init();
    this.bindShuffle();
    this.bindSearchPill();

    // Show the first screen in the chosen order right away (single fade-in).
    this.renderInitialBatch();

    // Enlarge the pool in the background for richer "Shuffle vibes" later. When
    // the inline pool was empty, this is also the primary loader and reveals the
    // SSR once ready.
    this.poolPromise = this.loadPool();
    if (!this.poolReady) {
      this.poolPromise.then(() => this.applyInitialBatch());
    }
  }

  // Pick a random collection (weighted) entirely on the CLIENT, so the choice
  // differs on every page load. Shopify caches the server-rendered section, so a
  // Liquid-side pick would be frozen per cache. We read the candidate list from
  // `data-collection-options` ("handle:weight", comma-separated), then grab that
  // collection's pre-inlined product pool and feed it straight to the first
  // screen — synchronous, no network round-trip.
  pickRandomCollection() {
    const opts = (this.element.dataset.collectionOptions || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const weighted = [];
    for (const o of opts) {
      const parts = o.split(':');
      const handle = (parts[0] || '').trim();
      let w = parseInt((parts[1] || '1').trim(), 10);
      if (!handle || isNaN(w) || w < 1) w = 1;
      for (let i = 0; i < w; i++) weighted.push(handle);
    }
    if (!weighted.length) return false;
    const chosen = weighted[Math.floor(Math.random() * weighted.length)];
    const node = this.element.querySelector(
      '[data-hero-pool="' + chosen + '"]'
    );
    if (!node) return false;
    try {
      const all = JSON.parse(node.textContent || '[]');
      if (!Array.isArray(all) || !all.length) return false;
      this.pool = all;
      this.poolReady = true;
      this.element.dataset.collectionHandle = chosen;
      return true;
    } catch (err) {
      console.error('[HeroMarquee] pool parse failed:', err);
      return false;
    }
  }

  // Pre-fetch the product pool used for client-side "shuffle" paging. We pull
  // the whole collection (one page at a time, up to `MAX_PAGES`). The collection
  // shown here was already chosen RANDOMLY (with weights) on the CLIENT side in
  // `pickRandomCollection()`, so its products arrive in that collection's own
  // curated order — no client-side re-sort. "Shuffle vibes" then behaves like a
  // true *next page* within the SAME collection's product pool.
  async loadPool() {
    const handle = this.element.dataset.collectionHandle;
    if (!handle) return;
    try {
      const base =
        '/collections/' + encodeURIComponent(handle) + '/products.json';
      const MAX_PAGES = 10;

      // Fetch every page in parallel — far quicker than awaiting them one by
      // one. A large collection used to take several seconds sequentially,
      // which made the first "Shuffle" click appear to hang.
      const responses = await Promise.all(
        Array.from({ length: MAX_PAGES }, (_, i) =>
          fetch(base + '?page=' + (i + 1) + '&limit=50')
        )
      );
      const all = [];
      for (const res of responses) {
        if (!res.ok) continue;
        const data = await res.json();
        const prods = data.products || [];
        if (prods.length) all.push.apply(all, prods);
      }

      // The chosen collection's products come back already in the collection's
      // own curated order (the same order the section inlines for the first
      // screen). No client-side re-sort is needed — the random COLLECTION choice
      // already provides the variety.

      this.pool = all;
      this.poolReady = true;
    } catch (err) {
      console.error('[HeroMarquee] pool load failed:', err);
    }
  }

  // Build the first-screen pool synchronously from the JSON the section inlines
  // into the page. This avoids a network round-trip, so the marquee's first
  // screen appears in the chosen random order immediately — never a delayed
  // "second load" of a different product set.
  loadPoolInline() {
    const node = this.element.querySelector('[data-hero-pool]');
    if (!node) return;
    try {
      const all = JSON.parse(node.textContent || '[]');
      if (!Array.isArray(all) || all.length === 0) return;
      // The inline pool is already in the chosen collection's curated order
      // (no client-side re-sort needed — see loadPool). Keep it as-is.

      this.pool = all;
      this.poolReady = true;
    } catch (err) {
      console.error('[HeroMarquee] inline pool parse failed:', err);
    }
  }

  // Render the FIRST screen from the inline pool (already in the chosen
  // collection's curated order) with a single fade-in. Because the pool is
  // available synchronously, the user never sees the SSR set and then a
  // *different* set a few seconds later — they see one ordering, once.
  renderInitialBatch() {
    if (!this.poolReady || this.pool.length === 0) {
      // No inline pool (e.g. empty collection): just reveal the SSR batch.
      this.tracks.forEach((t) => { t.style.opacity = '1'; });
      return;
    }
    const picked = this.pool.slice(0, this.batchSize);
    this.poolIndex = this.batchSize;
    // Tracks are visible immediately (skeleton cards paint on first paint, set in
    // the section CSS), so there is no blank gap. Here we just swap in the
    // chosen ordering; the SSR skeleton is replaced by the same skeleton in the
    // new order (invisible change), and the real images then fade in on load.
    this.swapBatch(picked);
  }

  // Fallback used only when the inline pool was empty: fade the SSR batch out,
  // swap in the fetched pool's first batch, and fade it back in.
  async applyInitialBatch() {
    if (this.isShuffling) return;
    this.isShuffling = true;
    try {
      if (!this.poolReady || this.pool.length === 0) {
        this.tracks.forEach((t) => { t.style.opacity = '1'; });
        return;
      }
      const picked = this.pool.slice(0, this.batchSize);
      // Shuffle continues *after* this first batch.
      this.poolIndex = this.batchSize;
      await this.setOpacity('0');
      this.swapBatch(picked);
      await this.setOpacity('1');
    } catch (err) {
      console.error('[HeroMarquee] initial batch swap failed:', err);
    } finally {
      this.isShuffling = false;
    }
  }

  // Swap the two tracks' cards to `picked` (no fade), duplicating for the
  // seamless loop. Callers wrap this in `setOpacity` fades so the (sometimes
  // slow) wait for the product pool is hidden underneath the faded-out state.
  // We recompute each row's geometry, reset its scroll position to the start,
  // and clear any in-progress drag state.
  swapBatch(picked) {
    const half = Math.ceil(picked.length / 2);
    const top = picked.slice(0, half);
    const bottom = picked.slice(half);
    this.inners[0].innerHTML = top.map(buildCard).join('');
    this.inners[1].innerHTML = bottom.map(buildCard).join('');
    this.duplicate();
    this.rows.forEach((row) => {
      row.track.classList.remove('is-dragging');
      row.dragging = false;
      row.setWidth = this.measureSetWidth(row);
      row.offset = row.dir < 0 ? 0 : -row.setWidth;
      row.track.style.transform = 'translateX(' + row.offset + 'px)';
    });
    // Approach C: reveal the whole batch together (soft fade-in) once its images
    // have loaded — instead of filling left-to-right as each image arrives.
    this.reveal();
  }

  // Approach C: softly reveal the current batch's images as a single unit, once
  // they have all loaded (with a timeout fallback so slow CDNs still reveal).
  reveal() {
    const imgs = Array.from(this.element.querySelectorAll('.hero-marquee__card img'));
    this.element.classList.remove('is-revealed');
    if (!imgs.length) {
      this.element.classList.add('is-revealed');
      return;
    }
    let remaining = imgs.length;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      this.element.classList.add('is-revealed');
    };
    imgs.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        if (--remaining === 0) finish();
        return;
      }
      const onEnd = () => { if (--remaining === 0) finish(); };
      img.addEventListener('load', onEnd, { once: true });
      img.addEventListener('error', onEnd, { once: true });
    });
    clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(finish, 3000);
  }

  // Width of one card set (used for the seamless wrap). Measured as the gap
  // between the first card and its clone so the loop stays perfectly aligned.
  measureSetWidth(row) {
    const kids = row.inner.children;
    const n = Math.floor(kids.length / 2);
    if (n > 0 && kids[n]) {
      return kids[n].offsetLeft - kids[0].offsetLeft;
    }
    return row.track.scrollWidth / 2;
  }

  // Set both tracks' opacity and resolve once the CSS transition has finished,
  // so callers can await a clean fade-out / fade-in.
  setOpacity(value) {
    this.tracks.forEach((track) => { track.style.opacity = value; });
    return new Promise((resolve) => setTimeout(resolve, 350));
  }

  init() {
    // Wrap each track's cards in an inner element (flex layout) and build a
    // per-row state object shared by the rAF loop and the drag handlers.
    this.tracks.forEach((track) => {
      let inner = track.querySelector('.hero-marquee__track-inner');
      if (!inner) {
        inner = document.createElement('div');
        inner.className = 'hero-marquee__track-inner';
        while (track.firstElementChild) inner.appendChild(track.firstElementChild);
        track.appendChild(inner);
      }
      this.inners.push(inner);
      const dir = track.classList.contains('hero-marquee__track--right') ? 1 : -1;
      this.rows.push({
        track, inner, dir,
        offset: 0,
        setWidth: 1,
        dragging: false,
        hover: false,
        baseOffset: 0,
        startX: 0,
        startY: 0,
        moved: false,
      });
    });

    // Duplicate cards for seamless loop, then measure geometry and start.
    this.duplicate();
    this.rows.forEach((row) => {
      row.setWidth = this.measureSetWidth(row);
      row.offset = row.dir < 0 ? 0 : -row.setWidth;
      row.track.style.transform = 'translateX(' + row.offset + 'px)';
    });

    this.bindDrag();
    this.startLoop();
  }

  duplicate() {
    this.inners.forEach((inner) => {
      const cards = inner.querySelectorAll('.hero-marquee__card');
      cards.forEach((card) => {
        const clone = card.cloneNode(true);
        inner.appendChild(clone);
      });
    });
  }

  bindShuffle() {
    const btn = this.element.querySelector('[data-hero-shuffle]');
    if (btn) {
      btn.addEventListener('click', () => this.shuffle());
    }
  }

  // The pill-shaped search trigger looks like a search field but is a <button>.
  // Clicking it opens the existing header search modal (Dawn's `details-modal`,
  // toggled by its <summary>). We trigger that summary programmatically so the
  // user gets the same predictive-search experience. If the header search isn't
  // present on the page, fall back to the vanilla /search results page.
  //
  // IMPORTANT: we must `stopPropagation()` on the pill's own click. Dawn's
  // `details-modal.open()` attaches a `document.body` click listener (to close
  // when clicking outside). If our pill click keeps bubbling to <body>, that
  // listener sees the pill (which is NOT inside the modal) and immediately
  // closes the modal we just opened — so nothing would appear.
  bindSearchPill() {
    const pill = this.element.querySelector('[data-hero-search-pill]');
    if (!pill) return;
    pill.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const modal = document.querySelector('details-modal.header__search');
      const trigger = (modal && modal.querySelector('summary')) ||
        document.querySelector('.header__icon--search');

      if (trigger) {
        trigger.click();
      } else {
        window.location.href = '/search';
      }
    });
  }

  // Manual swipe/drag on every row (touch + mouse). The scroll is driven by a
  // rAF loop (startLoop) which reads `row.offset`, so while dragging we only
  // mutate `row.offset` — the drag therefore PERSISTS (no snap-back). We
  // deliberately avoid setPointerCapture: capturing retargets the following
  // click to the track and breaks the card's <a> navigation. Instead we listen
  // on window for the drag's duration, keeping clicks on the anchor.
  bindDrag() {
    this.rows.forEach((row) => this.attachDrag(row));
  }

  attachDrag(row) {
    const track = row.track;
    const THRESHOLD = 10;

    const onMove = (e) => {
      if (!row.dragging) return;
      const delta = e.clientX - row.startX;
      const dy = e.clientY - row.startY;
      // Mostly-vertical gesture -> let the page scroll, abort the drag.
      if (!row.moved && Math.abs(dy) > Math.abs(delta) && Math.abs(dy) > THRESHOLD) {
        endDrag();
        return;
      }
      if (Math.abs(delta) > THRESHOLD) row.moved = true;
      row.offset = row.baseOffset + delta;
    };

    const endDrag = () => {
      if (!row.dragging) return;
      row.dragging = false;
      track.classList.remove('is-dragging');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    const onUp = () => endDrag();

    const onDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      row.dragging = true;
      row.moved = false;
      row.startX = e.clientX;
      row.startY = e.clientY;
      row.baseOffset = row.offset;
      track.classList.add('is-dragging');
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };

    const onClick = (e) => {
      if (row.moved) {
        e.preventDefault();
        e.stopPropagation();
        row.moved = false;
      }
    };

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('click', onClick, true);
    // Hover-pause (replaces the old CSS :hover rule, since the loop is JS-driven).
    track.addEventListener('mouseenter', () => { row.hover = true; });
    track.addEventListener('mouseleave', () => { row.hover = false; });
    // On desktop a mousedown+drag over an <img>/<a> starts the browser's native
    // drag-and-drop, which fires `pointercancel` and instantly kills our drag.
    // Cancelling `dragstart` keeps pointer events flowing (click still works).
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  // rAF loop: advances each row's offset when not dragging/hovering and applies
  // it as a transform every frame. Direction mirrors the old CSS marquee (left
  // row scrolls left, right row scrolls right), wrapping by one set width for a
  // seamless infinite loop.
  startLoop() {
    let last = performance.now();
    const step = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      this.rows.forEach((row) => {
        const sw = row.setWidth || 1;
        // Keep the *visual* speed constant across card counts: with 12 cards/row
        // the old CSS used 60s per set; more cards per row make `sw` bigger, so
        // scale the loop duration by perRow to hold the same px/sec.
        const loopSeconds = 60 * (this.perRow / 12);
        const v = sw / loopSeconds; // px/sec
        if (!row.dragging && !row.hover) {
          row.offset += row.dir * v * dt;
          if (row.dir < 0) {
            if (row.offset <= -sw) row.offset += sw;
            else if (row.offset > 0) row.offset -= sw;
          } else {
            if (row.offset >= 0) row.offset -= sw;
            else if (row.offset < -sw) row.offset += sw;
          }
        }
        row.track.style.transform = 'translateX(' + row.offset + 'px)';
      });
      this._raf = requestAnimationFrame(step);
    };
    this._raf = requestAnimationFrame(step);
  }

  async shuffle() {
    if (this.isShuffling) return;
    this.isShuffling = true;

    const btn = this.element.querySelector('[data-hero-shuffle]');
    if (btn) {
      btn.classList.add('is-loading');
      btn.disabled = true;
    }

    try {
      // Fade the current set out first, so any wait for the product pool reads
      // as an intentional part of the transition instead of a frozen screen.
      await this.setOpacity('0');

      // Reuse the single in-flight fetch started on init (never start a second
      // one, which is what used to make the first click hang for seconds).
      if (!this.poolReady) {
        await this.poolPromise;
      }
      if (!this.poolReady || this.pool.length === 0) {
        await this.setOpacity('1'); // restore if the pool never loaded
        return;
      }

      const batchSize = this.batchSize;
      const pool = this.pool;
      const total = pool.length;

      // Pick the next `batchSize` products starting at `poolIndex`, wrapping
      // around the end of the pool so the marquee never goes empty.
      let picked;
      if (total <= batchSize) {
        picked = pool.slice();
        this.poolIndex = 0;
      } else {
        picked = [];
        for (let i = 0; i < batchSize; i++) {
          picked.push(pool[(this.poolIndex + i) % total]);
        }
        this.poolIndex = (this.poolIndex + batchSize) % total;
      }

      // Swap underneath the cover of opacity 0, then fade the new set in.
      this.swapBatch(picked);
      await this.setOpacity('1');
    } catch (err) {
      console.error('[HeroMarquee] shuffle failed:', err);
    } finally {
      this.isShuffling = false;
      if (btn) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }
  }
}

// Extract the first usable image URL from a storefront `products.json` product.
// `featured_image` is a STRING; `images` is an array of URL strings. Each may
// also appear as an object with a `.src` property in some responses.
function extractImageSrc(p) {
  if (!p) return '';
  const candidates = [];
  if (p.featured_image) candidates.push(p.featured_image);
  if (Array.isArray(p.images)) {
    p.images.forEach((im) => candidates.push(im));
  }
  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    let url = '';
    if (typeof c === 'string') url = c;
    else if (c && typeof c === 'object') url = c.src || c.url || '';
    if (url) return imgUrl(url);
  }
  return '';
}

function imgUrl(src) {
  if (!src) return '';
  return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=400&height=533';
}

// Build a marquee card's HTML from a storefront `products.json` product.
function buildCard(p) {
  const href = p.url || '/products/' + p.handle;
  const title = (p.title || '').replace(/"/g, '&quot;');
  // In the storefront `products.json`, `featured_image` is a STRING (the
  // image URL). `images` is an array of URL strings. Support all shapes
  // defensively so a card never ends up imageless.
  const imgSrc = extractImageSrc(p);
  if (!imgSrc) {
    console.warn('[HeroMarquee] no image for product:', p && p.handle, p);
  }
  const img = imgSrc
    ? '<img src="' + imgSrc + '" width="400" height="533" alt="' + title + '" draggable="false">'
    : '';
  return (
    '<div class="hero-marquee__card"><a href="' +
    href +
    '" aria-label="' +
    title +
    '">' +
    img +
    '</a></div>'
  );
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-hero-marquee]').forEach((el) => {
    el.heroMarquee = new HeroMarquee(el);
  });
});

// Also initialize if section is loaded via Shopify AJAX
document.addEventListener('shopify:section:load', (event) => {
  const marquee = event.target.querySelector('[data-hero-marquee]');
  if (marquee) {
    marquee.heroMarquee = new HeroMarquee(marquee);
  }
});
